import { useId } from "react";

import {
  Accordion,
  ActionIcon,
  Divider,
  Group,
  Input,
  NumberInput,
  ScrollArea,
  Stack,
  TagsInput,
  TextInput,
} from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { IconCircleMinus, IconGripVertical, IconRefresh } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { DotPhraseRichTextField, PHRASE_TEXT_EXTENSIONS } from "@/dot-phrases";
import { toProcedureNoteHtml } from "@/encounters/utils/procedure-notes";
import { ProceduresPickerFormType } from "@/encounters/zod-schemas/procedures";
import { ServiceDateKey } from "@/shared/procedure-code-effective-dates";
import ProcedureCodeDateWarning from "@/shared/procedure-code-effective-dates/components/ProcedureCodeDateWarning";
import { normalizeEmptyHtml } from "@/utils/rich-text";

import ProceduresCodeBadge from "./ProceduresCodeBadge";

function SelectedDiagnosisRow({
  code,
  description,
  onRemove,
  isRemovable,
}: {
  code: string;
  description: string;
  onRemove: () => void;
  isRemovable: boolean;
}) {
  return (
    <Group
      px="sm"
      py="xs"
      gap="sm"
      bg="white"
      style={{ borderRadius: 8 }}
      bd="1px solid var(--mantine-color-cream-4)"
    >
      <ActionIcon
        size="sm"
        color="gray"
        variant="transparent"
        style={{ cursor: "grab" }}
        data-testid="grip-vertical-icon"
      >
        <IconGripVertical size={16} />
      </ActionIcon>
      <ProceduresCodeBadge code={code} />
      <TextWrapper flex={1}>{description}</TextWrapper>
      {isRemovable && (
        <ActionIcon
          size="sm"
          color="red"
          onClick={onRemove}
          variant="transparent"
          aria-label="Remove diagnosis"
          data-testid={`remove-diagnosis-button-${code}`}
        >
          <IconCircleMinus size={20} />
        </ActionIcon>
      )}
    </Group>
  );
}

function SelectedProcedureRow({
  code,
  description,
  index,
  form,
  onRemove,
  isRemovable,
  serviceDate,
}: {
  code: string;
  description: string;
  index: number;
  form: UseFormReturnType<ProceduresPickerFormType>;
  onRemove: () => void;
  isRemovable: boolean;
  /** The encounter's service date (`yyyy-MM-dd`); a code not billable on it shows a warning. */
  serviceDate?: ServiceDateKey;
}) {
  const noteLabelId = useId();

  return (
    <Stack gap={4}>
      <Accordion
        radius="sm"
        variant="separated"
        styles={{
          item: { border: "1px solid var(--mantine-color-cream-4)" },
          content: { paddingTop: 0 },
        }}
      >
        <Accordion.Item bg="white" value={code}>
          <Accordion.Control data-testid={`procedures-picker-procedure-${code}`}>
            <Group gap="sm" wrap="nowrap">
              <ProceduresCodeBadge code={code} />
              <TextWrapper flex={1}>{description}</TextWrapper>
              {isRemovable && (
                <ActionIcon
                  size="sm"
                  color="red"
                  tabIndex={0}
                  role="button"
                  component="div"
                  variant="transparent"
                  aria-label="Remove procedure"
                  data-testid={`remove-procedure-button-${code}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove();
                  }}
                >
                  <IconCircleMinus size={20} />
                </ActionIcon>
              )}
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <Input.Wrapper label="Procedure Note" labelProps={{ id: noteLabelId }}>
                <DotPhraseRichTextField
                  showToolbar={false}
                  aria-labelledby={noteLabelId}
                  placeholder="Add a procedure note"
                  extensions={PHRASE_TEXT_EXTENSIONS}
                  data-testid={`procedure-note-input-${code}`}
                  value={toProcedureNoteHtml(form.values.procedures[index].providerNotes)}
                  onChange={(value) =>
                    form.setFieldValue(
                      `procedures.${index}.providerNotes`,
                      normalizeEmptyHtml(value),
                    )
                  }
                />
              </Input.Wrapper>
              <Group grow>
                <NumberInput
                  min={0}
                  label="Unit"
                  allowNegative={false}
                  data-testid={`unit-input-${code}`}
                  {...form.getInputProps(`procedures.${index}.units`)}
                />
                <TagsInput
                  label="Modifier"
                  placeholder="Add a modifier"
                  data-testid={`modifier-input-${code}`}
                  {...form.getInputProps(`procedures.${index}.modifiers`)}
                />
              </Group>
              <TextInput
                label="Billing Note"
                placeholder="Add a billing note"
                data-testid={`billing-note-input-${code}`}
                {...form.getInputProps(`procedures.${index}.billerNotes`)}
              />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      <ProcedureCodeDateWarning
        code={code}
        serviceDate={serviceDate}
        data-testid={`procedure-warning-${code}`}
      />
    </Stack>
  );
}

function ProceduresPickerRightPanel({
  form,
  conditionsLocked = false,
  proceduresLocked = false,
  serviceDate,
}: {
  form: UseFormReturnType<ProceduresPickerFormType>;
  /** The picker opened on a single diagnosis, so the diagnosis selection cannot be changed. */
  conditionsLocked?: boolean;
  /** The picker opened on a single procedure code, so the procedure selection cannot be changed. */
  proceduresLocked?: boolean;
  /** The encounter's service date (`yyyy-MM-dd`); codes not billable on it show a warning. */
  serviceDate?: ServiceDateKey;
}) {
  const conditions = form.values.conditions;
  const procedures = form.values.procedures;

  // Clears everything this session is allowed to change. Whichever side is locked stays put.
  const handleClearAll = () => {
    form.setValues({
      conditions: conditionsLocked ? conditions : [],
      procedures: proceduresLocked ? procedures : [],
    });
    form.clearErrors();
  };

  return (
    <Stack
      p="md"
      flex={1}
      gap="md"
      bg="gray.0"
      style={{
        borderLeft: "1px solid var(--mantine-color-cream-4)",
        overflow: "hidden",
      }}
    >
      <Group justify="space-between">
        <TextWrapper fz="md" fw={600}>
          Selected ICD-10 & CPT Codes
        </TextWrapper>
        <Group
          gap="xs"
          onClick={handleClearAll}
          style={{ cursor: "pointer" }}
          data-testid="clear-all-button"
        >
          <TextWrapper fw={600} size="md" c="burgundy.9">
            Clear All
          </TextWrapper>
          <IconRefresh size={20} />
        </Group>
      </Group>

      <Divider />

      <ScrollArea flex={1} offsetScrollbars>
        <Stack gap="md">
          <Stack gap="xs">
            <TextWrapper fw={700} size="xs" c="gray.7" tt="uppercase" style={{ letterSpacing: 1 }}>
              Diagnoses
            </TextWrapper>
            {conditions.length === 0 ? (
              <TextWrapper size="md" c="black" variant="default">
                No diagnoses have been selected
              </TextWrapper>
            ) : (
              conditions.map((item, index) => (
                <SelectedDiagnosisRow
                  key={item.code}
                  code={item.code}
                  description={item.display}
                  isRemovable={!conditionsLocked}
                  onRemove={() => form.removeListItem("conditions", index)}
                />
              ))
            )}
          </Stack>

          <Stack gap="xs">
            <TextWrapper fw={700} size="xs" c="gray.7" tt="uppercase" style={{ letterSpacing: 1 }}>
              Procedures
            </TextWrapper>
            {procedures.length === 0 ? (
              <TextWrapper size="md" c="black" variant="default">
                No procedures have been selected
              </TextWrapper>
            ) : (
              procedures.map((item, index) => (
                <SelectedProcedureRow
                  form={form}
                  index={index}
                  key={item.code}
                  code={item.code}
                  serviceDate={serviceDate}
                  description={item.display}
                  isRemovable={!proceduresLocked}
                  onRemove={() => form.removeListItem("procedures", index)}
                />
              ))
            )}
          </Stack>
        </Stack>
      </ScrollArea>
    </Stack>
  );
}

export default ProceduresPickerRightPanel;
